import { QueryClient } from '@tanstack/react-query';
import debugLog from './debug';

// Default query options for all queries
const defaultQueryOptions = {
  queries: {
    // Stale time - how long data is considered fresh
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Cache time - how long data stays in cache after component unmounts
    gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime in v4)

    // Retry configuration
    retry: (failureCount: number, error: unknown) => {
      // Don't retry on 4xx errors (client errors)
      if (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        typeof (error as { status: number }).status === 'number' &&
        (error as { status: number }).status >= 400 &&
        (error as { status: number }).status < 500
      ) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },

    // Retry delay with exponential backoff
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),

    // Refetch on window focus (can be disabled per query)
    refetchOnWindowFocus: false,

    // Refetch on reconnect
    refetchOnReconnect: true,

    // Refetch on mount if data is stale
    refetchOnMount: true,
  },
  mutations: {
    // Retry mutations once on network errors
    retry: (failureCount: number, error: unknown) => {
      if (
        typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        (error as { name: string }).name === 'NetworkError' &&
        failureCount < 1
      ) {
        return true;
      }
      return false;
    },
  },
};

// Create and configure the query client
export const queryClient = new QueryClient({
  defaultOptions: defaultQueryOptions,
});

// Query keys factory for consistent key management
export const queryKeys = {
  // User-related queries
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.users.lists(), { filters }] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    profile: (id: string) =>
      [...queryKeys.users.detail(id), 'profile'] as const,
  },
} as const;

// Utility functions for query invalidation
export const invalidateQueries = {
  // Invalidate all user-related queries
  users: () => queryClient.invalidateQueries({ queryKey: queryKeys.users.all }),

  // Invalidate specific user
  user: (id: string) =>
    queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(id) }),
};

// Prefetch utilities
export const prefetchQueries = {
  // Prefetch user profile
  userProfile: async (id: string) => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.users.profile(id),
      queryFn: () => fetchUserProfile(id),
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  },
};

// Mock API functions (replace with actual API calls)
async function fetchUserProfile(id: string) {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  return {
    id,
    name: `User ${id}`,
    email: `user${id}@example.com`,
    avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${id}`,
    bio: `This is the bio for user ${id}`,
    joinedAt: new Date().toISOString(),
  };
}

// Error handling utilities
export const handleQueryError = (error: unknown) => {
  debugLog('debug', 'Query error:', error);

  // You can add global error handling here
  // For example, show toast notifications, redirect to login, etc.

  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as { status: number }).status === 'number' &&
    (error as { status: number }).status === 401
  ) {
    // Handle unauthorized errors
    debugLog('debug', 'Unauthorized access - redirecting to login');
    // Redirect to login or clear auth state
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as { status: number }).status === 'number' &&
    (error as { status: number }).status >= 500
  ) {
    // Handle server errors
    debugLog('debug', 'Server error occurred');
    // Show user-friendly error message
  }
};

// Development utilities
export const queryClientDevtools = {
  // Clear all queries
  clearAll: () => queryClient.clear(),

  // Get cache data for debugging
  getCacheData: () => queryClient.getQueryCache().getAll(),

  // Get specific query data
  getQueryData: (queryKey: unknown[]) => queryClient.getQueryData(queryKey),

  // Set query data manually (for testing)
  setQueryData: (queryKey: unknown[], data: unknown) =>
    queryClient.setQueryData(queryKey, data),
};
